import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Switch,
  Alert,
  Spin,
  Select,
  Checkbox,
  Row,
  Col,
  Divider,
  Empty,
  Tooltip
} from "antd";
import {
  BookOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckSquareOutlined,
  EditOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  createCourse,
  updateCourse,
  fetchCourseDetails,
  updateCoursePrerequisites,
  getCategories
} from "../Services/CourseListService";
import { useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import api from "Common/Client/Interceptor";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Add API function to get authors list
const getAuthorsList = async () => {
  try {
    console.log(" Fetching authors list...");
    
    const response = await api.get("/indv/CourseAurthor/Get-List");
    
    console.log(" Authors data received:", response.data);
    return response.data;
  } catch (error) {
    console.error(" Error fetching authors:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    throw error;
  }
};

// LinkedIn URL validation function
const isValidLinkedInUrl = (url) => {
  if (!url) return false;
  
  // Regular expression for LinkedIn profile URLs
  const linkedInRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
  
  return linkedInRegex.test(url);
};

// Add function to get subcategories
const getSubCategories = async (categoryId) => {
  try {
    // First, get category details which includes subcategories
    const response = await api.get(`/indv/CourseCategory/get-for-update/${categoryId}`);
    
    // The API response has a subCategories array
    if (response.data && response.data.subCategories) {
      return response.data.subCategories;
    }
    
    return [];
  } catch (error) {
    console.error(" Error fetching subcategories:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    throw error;
  }
};

const CreateCoursePage = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [prerequisites, setPrerequisites] = useState([]);
  const [currentPrerequisite, setCurrentPrerequisite] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  // Author related states
  const [authors, setAuthors] = useState([]);
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [selectedAuthorId, setSelectedAuthorId] = useState(null);
  const [showExistingAuthors, setShowExistingAuthors] = useState(false);
  const [showNewAuthorForm, setShowNewAuthorForm] = useState(false);
  const [newAuthor, setNewAuthor] = useState({
    name: "",
    email: "",
    linkedInProfile: "",
    bio: ""
  });

  // Subcategories states
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [availableSubCategories, setAvailableSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  
  const isPaid = Form.useWatch("isPaid", form);



  const isEditMode = !!courseId;
  const location = useLocation();

  const prerequisitesRef = useRef(null);

  useEffect(() => {
  console.log("🟡 isPaid (initial / current):", isPaid);
}, [isPaid]);


  // Set mounted state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Load categories and authors on component mount
  useEffect(() => {
    loadCategories();
    loadAuthors();
  }, []);

  // Load course data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadCourseData();
    }
  }, [courseId]);

  
  // Load subcategories when category is selected
const loadSubCategories = async (categoryId) => {
  if (!categoryId) {
    setAvailableSubCategories([]);
    setSelectedSubCategories([]);
    return;
  }

  setLoadingSubCategories(true);
  try {
    const subCategories = await getSubCategories(categoryId);
    setAvailableSubCategories(subCategories);
    
    // If we're in edit mode and already have selected subcategories,
    // keep only those that exist in the new category
    if (isEditMode && selectedSubCategories.length > 0) {
      // Check if any of the currently selected subcategories belong to the new category
      const preservedSelections = selectedSubCategories.filter(sub => 
        subCategories.some(newSub => newSub.id === sub.id && newSub.parentCategoryId === categoryId)
      );
      setSelectedSubCategories(preservedSelections);
    }
  } catch (error) {
    console.error('Error loading subcategories:', error);
    toast.error('Failed to load subcategories');
    setAvailableSubCategories([]);
    setSelectedSubCategories([]);
  } finally {
    setLoadingSubCategories(false);
  }
};

  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const data = await getCategories();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadAuthors = async () => {
    setAuthorsLoading(true);
    try {
      const data = await getAuthorsList();
      setAuthors(data.items || []);
    } catch (error) {
      console.error('Error loading authors:', error);
      setAuthors([]);
    } finally {
      setAuthorsLoading(false);
    }
  };

const loadCourseData = async () => {
  setPageLoading(true);
  try {
    const data = await fetchCourseDetails(courseId);
    setCourseData(data);
    console.log("Loaded course data:", data);
    console.log("Course category:", data.category);
    console.log("Course subcategories:", data.category?.subCategories);

    // Set form values
    form.setFieldsValue({
      title: data.title,
      description: data.description,
      categoryId: data.categoryId,
      isPaid: Boolean(data.isPaid),
  price: data.pricing?.price,
  accessDuration: data.pricing?.accessDurationDays
    
    });

    // Set initial category ID
    setSelectedCategoryId(data.categoryId);
    
    // Load subcategories from API for this category
    const subCategories = await getSubCategories(data.categoryId);
    setAvailableSubCategories(subCategories);
    console.log("Available subcategories from API:", subCategories);
    
    // FIXED: Check if course has subcategories from the API response
    // The API response shows subcategories are inside data.category.subCategories
    if (data.category && data.category.subCategories && data.category.subCategories.length > 0) {
      console.log("Found subcategories in course data:", data.category.subCategories);
      
      // Map the subcategory data to match the structure from getSubCategories
      const courseSubCategories = data.category.subCategories.map(sub => ({
        id: sub.id,
        name: sub.name,
        description: sub.description,
        isMainCategory: sub.isMainCategory,
        parentCategoryId: sub.parentCategoryId,
        isUsed: sub.isUsed,
        createdOn: sub.createdOn,
        lastModifiedOn: sub.lastModifiedOn
      }));
      
      console.log("Mapped course subcategories:", courseSubCategories);
      setSelectedSubCategories(courseSubCategories);
    } else {
      console.log("No subcategories found in course data");
      setSelectedSubCategories([]);
    }

    // Set author information
    if (isEditMode) {
      setShowExistingAuthors(true);
      setShowNewAuthorForm(false);
      
      // Set the selected author ID
      if (data.authorId) {
        setSelectedAuthorId(data.authorId);
        console.log("Set author ID from authorId:", data.authorId);
      } else if (data.courseAuthorId) {
        setSelectedAuthorId(data.courseAuthorId);
        console.log("Set author ID from courseAuthorId:", data.courseAuthorId);
      } else if (data.courseAuthor && data.courseAuthor.id) {
        setSelectedAuthorId(data.courseAuthor.id);
        console.log("Set author ID from courseAuthor.id:", data.courseAuthor.id);
      } else {
        console.log("No author ID found in course data");
      }
      
      // Store author details for display
      setNewAuthor({
        name: data.authorName || data.courseAuthor?.name || "",
        email: data.authorEmail || data.courseAuthor?.email || "",
        linkedInProfile: data.authorLinkedIn || data.courseAuthor?.linkedInProfile || "",
        bio: data.authorBio || data.courseAuthor?.bio || ""
      });
    }

    // Set prerequisites
    if (data.coursePrerequisites && data.coursePrerequisites.length > 0) {
      const prereqStrings = data.coursePrerequisites.map(p => {
        if (typeof p === 'string') return p;
        if (p.description) return p.description;
        return String(p);
      });
      setPrerequisites(prereqStrings);
    }

  } catch (error) {
    toast.error("Failed to load course data: " + (error.message || "Unknown error"));
    navigate("/proAdmin-dashboard/courses");
  } finally {
    setPageLoading(false);
  }
};

  
  // Handle category change
const handleCategoryChange = (categoryId) => {
  if (categoryId === selectedCategoryId) {
    return; // Same category, no need to reload
  }
  
  setSelectedCategoryId(categoryId);
  
    if (isEditMode && categoryId !== courseData?.categoryId) {
    setSelectedSubCategories([]);
  }
  
  loadSubCategories(categoryId);
};

  // Handle subcategory selection
  const handleSubCategorySelect = (subCategory) => {
    // Check if already selected
    if (selectedSubCategories.some(selected => selected.id === subCategory.id)) {
      // Remove if already selected
      setSelectedSubCategories(prev => 
        prev.filter(selected => selected.id !== subCategory.id)
      );
    } else {
      // Add if not selected
      setSelectedSubCategories(prev => [...prev, subCategory]);
    }
  };

  // Handle removing a selected subcategory
  const handleRemoveSubCategory = (subCategoryId) => {
    setSelectedSubCategories(prev => 
      prev.filter(sub => sub.id !== subCategoryId)
    );
  };

  const handleAddPrerequisite = () => {
    if (currentPrerequisite.trim() === "") {
      toast.error("Please enter a prerequisite");
      return;
    }

    if (prerequisites.includes(currentPrerequisite.trim())) {
      toast.warning("This prerequisite already exists");
      return;
    }

    setPrerequisites(prev => [...prev, currentPrerequisite.trim()]);
    setCurrentPrerequisite("");
    toast.success("Prerequisite added successfully!");
  };

  const handleRemovePrerequisite = (index) => {
    setPrerequisites(prev => prev.filter((_, i) => i !== index));
    toast.info("Prerequisite removed");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPrerequisite();
    }
  };

  const handleExistingAuthorChange = (e) => {
    const checked = e.target.checked;
    setShowExistingAuthors(checked);
    
    if (checked) {
      setShowNewAuthorForm(false);
    } else {
      setSelectedAuthorId(null);
    }
  };

  const handleNewAuthorChange = (e) => {
    const checked = e.target.checked;
    setShowNewAuthorForm(checked);
    
    if (checked) {
      setShowExistingAuthors(false);
      setSelectedAuthorId(null);
    } else {
      setNewAuthor({
        name: "",
        email: "",
        linkedInProfile: "",
        bio: ""
      });
    }
  };

  const handleAuthorSelect = (authorId) => {
    setSelectedAuthorId(authorId);
  };

  const handleNewAuthorInputChange = (field, value) => {
    setNewAuthor(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLinkedInBlur = () => {
    if (newAuthor.linkedInProfile && !isValidLinkedInUrl(newAuthor.linkedInProfile)) {
      toast.error("Please enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/username)");
    }
  };

  const validateAuthorSelection = () => {
    if (!isEditMode) {
      if (!showExistingAuthors && !showNewAuthorForm) {
        toast.error("Please select an existing author or create a new one");
        return false;
      }

      if (showExistingAuthors && !selectedAuthorId) {
        toast.error("Please select an author from the list");
        return false;
      }

      if (showNewAuthorForm) {
        if (!newAuthor.name.trim()) {
          toast.error("Author name is required");
          return false;
        }
        if (!newAuthor.email.trim()) {
          toast.error("Author email is required");
          return false;
        }
        if (!newAuthor.linkedInProfile.trim()) {
          toast.error("Author LinkedIn profile is required");
          return false;
        }
        if (!isValidLinkedInUrl(newAuthor.linkedInProfile)) {
          toast.error("Please enter a valid LinkedIn profile URL");
          return false;
        }
      }
    } else {
      // FIXED: More robust validation for edit mode
      if (!selectedAuthorId) {
        toast.error("Please select an author from the list");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (values) => {
    if (!values.categoryId) {
      toast.error("Please select a category for the course");
      return;
    }

    // Validate subcategories selection
    if (selectedSubCategories.length === 0) {
      toast.error("Please select at least one subcategory");
      return;
    }

    // Validate author selection
    if (!validateAuthorSelection()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare subcategories array with IDs
      const subCategoriesArray = selectedSubCategories.map(sub => sub.id);

      if (isEditMode) {
        // FIXED: Create update payload that matches the API expected structure
        // Ensure all required fields are included and not null
        console.log("Selected author ID before creating update payload:", selectedAuthorId);
        console.log("Selected subcategories:", subCategoriesArray);
        
        const updateData = {
          courseId: parseInt(courseId),
          title: values.title || "",
          description: values.description || "",
          categoryId: values.categoryId || 0,
          authorId: selectedAuthorId || (courseData?.authorId || 0), // FIXED: Use courseData.authorId as fallback
          subCategories: subCategoriesArray, // Include selected subcategories
          forceUpdateIfPublished: forceUpdate || false
        };

        console.log("Sending update data:", updateData);
        
        // Make sure we're not sending any null values
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === null || updateData[key] === undefined) {
            console.warn(`Field ${key} is null or undefined, setting to default value`);
            if (key === 'title' || key === 'description') {
              updateData[key] = "";
            } else if (key === 'categoryId' || key === 'authorId') {
              updateData[key] = 0;
            } else if (key === 'subCategories') {
              updateData[key] = [];
            } else if (key === 'forceUpdateIfPublished') {
              updateData[key] = false;
            }
          }
        });

        console.log("Final update data after null checks:", updateData);
        await updateCourse(updateData);

        // Always update prerequisites, even if empty
        try {
          await updateCoursePrerequisites(courseId, prerequisites, forceUpdate);
          toast.success("Course and prerequisites updated successfully!");
        } catch (prereqError) {
          console.error("Prerequisites update error:", prereqError);
          toast.error("Course updated but failed to update prerequisites");
        }
      } else {
        // FIXED: Create new course with proper author structure
        const coursePayload = {
          title: values.title,
          description: values.description,
          categoryId: values.categoryId,
          subCategories: subCategoriesArray, // Include selected subcategories
          prerequisites: prerequisites,
          isPaid:Boolean(values.isPaid),
         };

              if (values.isPaid) {
  coursePayload.pricing = {
    price: Number(values.price),
    accessDurationDays: Number(values.accessDuration)
  };
              }
        // Add author information based on selection
        if (showExistingAuthors && selectedAuthorId) {
          // For existing author, use courseAuthorId field
          coursePayload.courseAuthorId = selectedAuthorId;
          console.log("Using existing author with ID:", selectedAuthorId);
        } else if (showNewAuthorForm) {
          // For new author, use courseAuthor object
          coursePayload.courseAuthor = {
            name: newAuthor.name.trim(),
            email: newAuthor.email.trim(),
            linkedInProfile: newAuthor.linkedInProfile.trim(),
            bio: newAuthor.bio.trim()
          };
          console.log("Creating new author:", coursePayload.courseAuthor);
        } else {
          // This should not happen due to validation, but just in case
          throw new Error("Please select an existing author or create a new one");
        }

        console.log("..................Creating course with payload: in creare sourse pagae", coursePayload);
        const created = await createCourse(coursePayload);
        toast.success("Course created successfully!");

        // Save prerequisites after course creation
        if (prerequisites.length > 0) {
          try {
            await updateCoursePrerequisites(created.id, prerequisites, false);
            toast.success("Prerequisites added successfully!");
          } catch (err) {
            toast.error("Course created but failed to save prerequisites.");
          }
        }
      }

      setTimeout(() => {
        navigate("/proAdmin-dashboard/courses");
      }, 1500);

    } catch (error) {
      console.error("Update error:", error);
      console.error("Error response data:", error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} course: ${errorMessage}`);
      console.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/proAdmin-dashboard/courses");
  };

  const getHeaderTitle = () => {
    return isEditMode ? "Edit Course" : "Create New Course";
  };

  const getSubmitButtonText = () => {
    return isEditMode ? "Update Course" : "Create Course";
  };

  const getSubmitButtonIcon = () => {
    return isEditMode ? <SaveOutlined /> : <PlusOutlined />;
  };

  if (pageLoading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 16 }}>Loading course data...</Title>
      </div>
    );
  }

  return (
    <Card
      className="card-professional"
      title={
        <div>
          <BookOutlined /> {getHeaderTitle()}
          {isEditMode && courseData && (
            <Tag color="blue" style={{ marginLeft: 8, fontSize: '14px' }}>
              ID: {courseId}
            </Tag>
          )}
        </div>
      }
      extra={
        <Button
          type="default"
          onClick={handleBack}
        >
          Back to Courses
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        {/* Information Card */}
        <Card style={{ background: "#f1f3f6ff" }}>
          <Title level={4}>💡 {isEditMode ? 'Course Editing' : 'Course Creation'} Tips</Title>
          <Space direction="vertical" size="small">
            <Text><strong>Title:</strong> Make it clear and descriptive</Text>
            <Text><strong>Description:</strong> Explain what students will achieve</Text>
            <Text><strong>Category:</strong> Select the most relevant category for your course</Text>
            <Text><strong>Subcategories:</strong> Select at least one subcategory (required)</Text>
            <Text><strong>Author:</strong> {isEditMode ? 'Select an author for this course' : 'Select an existing author or create a new one'}</Text>
            <Text><strong>Prerequisites:</strong> List required skills/knowledge</Text>
            {isEditMode && courseData?.isPublished && (
              <Alert
                message="Note"
                description="Changes to published courses will affect all enrolled students."
                type="info"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
            <Text type="secondary">
              {isEditMode
                ? "You can update the course title, description, category, subcategories, author, and prerequisites."
                : "You can add modules and content after creating the course."
              }
            </Text>
          </Space>
        </Card>

        {/* Published Course Warning */}
        {isEditMode && courseData?.isPublished && (
          <Alert
            message="Published Course"
            description="This course is currently published. Changes will affect enrolled students."
            type="warning"
            showIcon
            action={
              <Space direction="vertical">
                <Switch
                  checked={forceUpdate}
                  onChange={setForceUpdate}
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Force Update
                </Text>
              </Space>
            }
          />
        )}

        {/* Course Creation/Edit Form */}
        <Card style={{ background: "#f1f3f6ff" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
            initialValues={{
              isPaid:false
            }}
             onValuesChange={(changedValues, allValues) => {
    // Debug: Log form value changes
    if (changedValues.isPaid !== undefined) {
      console.log("isPaid changed to:", changedValues.isPaid);
    }
  }}
          >

            {/* Category Selection */}
            <Form.Item
              name="categoryId"
              label="Course Category"
              rules={[
                { required: true, message: "Please select a category for the course" }
              ]}
            >
              <Select
                placeholder={categoriesLoading ? "Loading categories..." : "Select a category"}
                loading={categoriesLoading}
                size="large"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase()) || false
                }
                notFoundContent={
                  categoriesLoading ?
                    <Spin size="small" /> :
                    categories.length === 0 ?
                      "No categories available" :
                      "No categories found"
                }
                disabled={categories.length === 0 && !categoriesLoading}
                onChange={handleCategoryChange}
              >
                {categories.map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
{/* Subcategories Selection */}
<Form.Item
  label={
    <Space>
      <span>Subcategories</span>
      <Tooltip title="Select at least one subcategory for your course">
        <InfoCircleOutlined style={{ color: '#1890ff' }} />
      </Tooltip>
      {selectedCategoryId && (
        <Tag color="blue" style={{ fontSize: '12px' }}>
          {loadingSubCategories ? "Loading..." : `${selectedSubCategories.length} selected`}
        </Tag>
      )}
    </Space>
  }
  required
  help={
    selectedCategoryId 
      ? selectedSubCategories.length === 0 
        ? "Please select at least one subcategory" 
        : `${selectedSubCategories.length} subcategory${selectedSubCategories.length !== 1 ? 's' : ''} selected`
      : "Select a category first to see available subcategories"
  }
  validateStatus={selectedCategoryId && selectedSubCategories.length === 0 ? "error" : ""}
>
  {selectedCategoryId ? (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* Show message if no subcategories selected */}
      {selectedSubCategories.length === 0 && (
        <Alert
          message="No subcategories selected"
          description="Please select at least one subcategory from the list below."
          type="warning"
          showIcon
        />
      )}

      {/* Show selected subcategories first */}
      {selectedSubCategories.length > 0 && (
        <Card 
          size="small" 
          title={
            <Space>
              <CheckSquareOutlined style={{ color: '#52c41a' }} />
              <span>Selected Subcategories ({selectedSubCategories.length})</span>
            </Space>
          }
          style={{ borderColor: '#52c41a' }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Click the X icon to remove a subcategory
            </Text>
          </div>
          <Space wrap>
            {selectedSubCategories.map(sub => (
              <Tag
                key={sub.id}
                closable
                onClose={() => handleRemoveSubCategory(sub.id)}
                color="green"
                style={{ 
                  padding: '6px 10px', 
                  marginBottom: 4,
                  border: '1px solid #52c41a',
                  fontSize: '14px'
                }}
              >
                <Space size={4}>
                  <CheckSquareOutlined style={{ fontSize: '12px' }} />
                  <Text strong>{sub.name}</Text>
                </Space>
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* Available Subcategories */}
      <Card 
        size="small" 
        title={
          <Space>
            <PlusOutlined style={{ color: '#1890ff' }} />
            <span>Available Subcategories {availableSubCategories.length > 0 && `(${availableSubCategories.length})`}</span>
          </Space>
        }
        loading={loadingSubCategories}
      >
        {availableSubCategories.length > 0 ? (
          <>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Click to add subcategories to your selection. Green items are already selected.
              </Text>
            </div>
            
            <Space wrap>
              {availableSubCategories.map(sub => {
                const isSelected = selectedSubCategories.some(selected => selected.id === sub.id);
                return (
                  <Tag
                    key={sub.id}
                    color={isSelected ? "green" : "blue"}
                    onClick={() => handleSubCategorySelect(sub)}
                    style={{
                      cursor: 'pointer',
                      padding: '6px 10px',
                      marginBottom: 4,
                      fontSize: '14px',
                      border: isSelected ? '2px solid #52c41a' : '1px solid #1890ff',
                      opacity: isSelected ? 0.8 : 1
                    }}
                  >
                    {isSelected ? (
                      <Space size={4}>
                        <CheckSquareOutlined style={{ fontSize: '12px' }} />
                        <Text>{sub.name}</Text>
                        <CloseOutlined 
                          style={{ fontSize: '10px', marginLeft: 4 }} 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSubCategory(sub.id);
                          }}
                        />
                      </Space>
                    ) : (
                      <Space size={4}>
                        <PlusOutlined style={{ fontSize: '12px' }} />
                        <Text>{sub.name}</Text>
                      </Space>
                    )}
                  </Tag>
                );
              })}
            </Space>
            
            
          </>
        ) : (
          <Empty
            description={
              <Text type="secondary">
                No subcategories available for this category. Please select a different category.
              </Text>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </Space>
  ) : (
    <Alert
      message="Select a Category First "
      description="Please select a category above to see available subcategories"
      type="info"
      showIcon
    />
  )}
</Form.Item>

            {/* Course Title */}
            <Form.Item
              name="title"
              label="Course Title"
              rules={[
                { required: true, message: "Please enter course title" },
                { min: 3, message: "Title must be at least 3 characters" },
                { max: 100, message: "Title must not exceed 100 characters" }
              ]}
            >
              <Input
                placeholder="Enter course title (e.g., 'Advanced Machine Learning')"
                size="large"
              />
            </Form.Item>

            {/* Course Description */}
            <Form.Item
              name="description"
              label="Course Description"
              rules={[
                { required: true, message: "Please enter course description" },
                { min: 10, message: "Description must be at least 10 characters" },
                { max: 1000, message: "Description must not exceed 1000 characters" }
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Describe what students will learn in this course, the target audience, and key objectives..."
                showCount
                maxLength={1000}
              />
            </Form.Item>

            {/* Author Selection Section */}
            <Form.Item
              label={
                <Space>
                  <UserOutlined />
                  <span>Course Author</span>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Text type="secondary">
                  {isEditMode 
                    ? "Select the author for this course" 
                    : "Select an existing author or create a new one for this course"
                  }
                </Text>

                {isEditMode ? (
                  // Edit mode: only show existing author selection
                  <Card size="small">
                    {authorsLoading ? (
                      <Spin size="small" />
                    ) : authors.length > 0 ? (
                      <Select
                        placeholder="Select an author"
                        value={selectedAuthorId}
                        onChange={handleAuthorSelect}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) || false
                        }
                      >
                        {authors.map(author => (
                          <Option key={author.id} value={author.id}>
                            {author.name} ({author.email})
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Alert
                        message="No authors available"
                        description="There are no existing authors in the system."
                        type="info"
                        size="small"
                      />
                    )}
                  </Card>
                ) : (
                  // Create mode: show both options
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card 
                        size="small" 
                        style={{ 
                          border: showExistingAuthors ? '2px solid #1890ff' : '1px solid #d9d9d9',
                          backgroundColor: showExistingAuthors ? '#f0f5ff' : '#fff'
                        }}
                      >
                        <Checkbox
                          checked={showExistingAuthors}
                          onChange={handleExistingAuthorChange}
                        >
                          <Text strong>Select Existing Author</Text>
                        </Checkbox>
                        
                        {showExistingAuthors && (
                          <div style={{ marginTop: 16 }}>
                            {authorsLoading ? (
                              <Spin size="small" />
                            ) : authors.length > 0 ? (
                              <Select
                                placeholder="Select an author"
                                value={selectedAuthorId}
                                onChange={handleAuthorSelect}
                                style={{ width: '100%' }}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option?.children?.toLowerCase().includes(input.toLowerCase()) || false
                                }
                              >
                                {authors.map(author => (
                                  <Option key={author.id} value={author.id}>
                                    {author.name} ({author.email})
                                  </Option>
                                ))}
                              </Select>
                            ) : (
                              <Alert
                                message="No authors available"
                                description="There are no existing authors in the system."
                                type="info"
                                size="small"
                              />
                            )}
                          </div>
                        )}
                      </Card>
                    </Col>
                    
                    <Col span={12}>
                      <Card 
                        size="small" 
                        style={{ 
                          border: showNewAuthorForm ? '2px solid #1890ff' : '1px solid #d9d9d9',
                          backgroundColor: showNewAuthorForm ? '#f0f5ff' : '#fff'
                        }}
                      >
                        <Checkbox
                          checked={showNewAuthorForm}
                          onChange={handleNewAuthorChange}
                        >
                          <Text strong>Create New Author</Text>
                        </Checkbox>
                        
                        {showNewAuthorForm && (
                          <div style={{ marginTop: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <Form.Item
                                label="Author Name"
                                required
                                help={!newAuthor.name.trim() ? "Author name is required" : ""}
                              >
                                <Input
                                  placeholder="Enter author name"
                                  value={newAuthor.name}
                                  onChange={(e) => handleNewAuthorInputChange('name', e.target.value)}
                                />
                              </Form.Item>
                              
                              <Form.Item
                                label="Author Email"
                                required
                                help={!newAuthor.email.trim() ? "Author email is required" : ""}
                              >
                                <Input
                                  placeholder="Enter author email"
                                  value={newAuthor.email}
                                  onChange={(e) => handleNewAuthorInputChange('email', e.target.value)}
                                />
                              </Form.Item>
                              
                              <Form.Item
                                label="LinkedIn Profile"
                                required
                                help={!newAuthor.linkedInProfile.trim() ? "LinkedIn profile is required" : 
                                  newAuthor.linkedInProfile && !isValidLinkedInUrl(newAuthor.linkedInProfile) ? 
                                  "Please enter a valid LinkedIn profile URL" : ""}
                              >
                                <Input
                                  placeholder="https://www.linkedin.com/in/username"
                                  value={newAuthor.linkedInProfile}
                                  onChange={(e) => handleNewAuthorInputChange('linkedInProfile', e.target.value)}
                                  onBlur={handleLinkedInBlur}
                                />
                              </Form.Item>
                              
                              <Form.Item label="Author Bio (Optional)">
                                <TextArea
                                  rows={3}
                                  placeholder="Enter author bio"
                                  value={newAuthor.bio}
                                  onChange={(e) => handleNewAuthorInputChange('bio', e.target.value)}
                                  maxLength={500}
                                  showCount
                                />
                              </Form.Item>
                            </Space>
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>
                )}
              </Space>
            </Form.Item>

             {/* Paid Switch */}
<Form.Item
  label="Is this a paid course?"
  name="isPaid"
  valuePropName="checked"
>
  <Switch
    onChange={(checked) => {
      console.log("Switch changed to:", checked);
    }}
  />
</Form.Item>


{isPaid && (
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Price"
        name="price"
        rules={[
          { required: true, message: "Price is required for paid courses" }
        ]}
      >
        <Input type="number" prefix="$" placeholder="Enter price" />
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item
        label="Access Duration (Days)"
        name="accessDuration"
        rules={[
          { required: true, message: "Access duration is required" }
        ]}
      >
        <Input type="number" placeholder="e.g. 365" />
      </Form.Item>
    </Col>
  </Row>
)}



            {/* Prerequisites Section */}
            <Form.Item
              label={
                <Space>
                  <CheckSquareOutlined />
                  <span>Prerequisites</span>
                  {isEditMode && <EditOutlined style={{ color: '#1890ff' }} />}
                </Space>
              }
            >
              <div
                ref={prerequisitesRef}
                id="prerequisites-section"
                style={{ scrollMarginTop: '20px' }}
              >
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <Text type="secondary">
                    {isEditMode
                      ? "Edit skills or knowledge required before taking this course"
                      : "Add skills or knowledge required before taking this course"
                    }
                  </Text>

                  {/* Add Prerequisite Input */}
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      placeholder="Enter prerequisite (e.g., 'Basic Python knowledge', 'Understanding of algebra')"
                      value={currentPrerequisite}
                      onChange={(e) => setCurrentPrerequisite(e.target.value)}
                      onKeyPress={handleKeyPress}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddPrerequisite}
                    >
                      Add
                    </Button>
                  </Space.Compact>

                  {/* Prerequisites List */}
                  {prerequisites.length > 0 ? (
                    <Card size="small" title={`Prerequisites (${prerequisites.length})`}>
                      <List
                        size="small"
                        dataSource={prerequisites}
                        renderItem={(prerequisite, index) => (
                          <List.Item
                            key={index}
                            actions={[
                              <Button
                                key="delete"
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemovePrerequisite(index)}
                              />
                            ]}
                          >
                            <Space>
                              <CheckSquareOutlined style={{ color: '#52c41a' }} />
                              <Text>{prerequisite}</Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Card>
                  ) : (
                    <Card size="small">
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        <Text type="secondary">No prerequisites added yet</Text>
                      </div>
                    </Card>
                  )}
                </Space>
              </div>
            </Form.Item>

            {/* Form Actions */}
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={getSubmitButtonIcon()}
                  disabled={selectedSubCategories.length === 0}
                >
                  {getSubmitButtonText()}
                </Button>
                <Button
                  onClick={handleBack}
                  size="large"
                >
                  Cancel
                </Button>
              </Space>
              {selectedSubCategories.length === 0 && selectedCategoryId && (
                <div style={{ marginTop: 8 }}>
                  <Alert
                    message="Subcategories Required"
                    description="Please select at least one subcategory before submitting"
                    type="error"
                    showIcon
                  />
                </div>
              )}
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </Card>
  );
};

export default CreateCoursePage;